import { useEffect, useState } from "react";
import { api } from "../api";
import { Badge, Btn, Card, Field, Input, Select, Spinner, Toggle } from "../components/Shared";

// Tags and extra fields: what a facility decides its own projects look like.
// Both are per group on purpose — the Metabolomics Lab has no use for
// "Organism", and the Bioinformatics Lab has no use for whatever they need.

export function TagsPanel({ groupId, canEdit }) {
  const [tags, setTags] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/api/groups/${groupId}/tags`).then(setTags).catch(e => setError(e.message));
  // `load` is redefined every render; listing it would refetch on
  // every keystroke elsewhere in the component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTags(null); load(); }, [groupId]);

  const add = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/groups/${groupId}/tags`, { name });
      setName("");
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const remove = async tag => {
    setBusy(true); setError(null);
    try {
      await api.delete(`/api/groups/${groupId}/tags/${tag.id}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Tags</h3>
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        Labels for this group's projects. A project can carry any number of them, and they
        are what the Projects page filters on.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}

      {tags === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div> : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: canEdit ? 16 : 0 }}>
          {tags.length === 0 && <span style={{ color: "#6b7280", fontSize: 13 }}>No tags yet.</span>}
          {tags.map(t => (
            <span key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Badge color={t.colour || "blue"}>{t.name}</Badge>
              {canEdit && (
                <button onClick={() => remove(t)} disabled={busy}
                        aria-label={`Remove tag ${t.name}`}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 15, padding: "0 2px" }}>×</button>
              )}
            </span>
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: "0 1 260px" }}>
            <Field label="New tag">
              <Input value={name} onChange={e => setName(e.target.value)}
                     placeholder="urgent, metagenomics, 2026…"
                     onKeyDown={e => { if (e.key === "Enter" && name.trim()) add(); }} />
            </Field>
          </div>
          <Btn onClick={add} disabled={busy || !name.trim()} style={{ marginBottom: 2 }}>
            {busy ? <><Spinner /> Working…</> : "Add tag"}
          </Btn>
        </div>
      )}
      {canEdit && (
        <p style={{ margin: "10px 0 0", color: "#6b7280", fontSize: 12 }}>
          Removing a tag takes it off every project that carried it. The log records how many.
        </p>
      )}
    </Card>
  );
}

const TYPES = [
  { value: "text",     label: "Text" },
  { value: "longtext", label: "Long text" },
  { value: "number",   label: "Number" },
  { value: "date",     label: "Date" },
  { value: "choice",   label: "Choice" },
  { value: "boolean",  label: "Yes / no" },
];

export function ProjectFieldsPanel({ groupId, canEdit }) {
  const [fields, setFields] = useState(null);
  const [draft, setDraft] = useState({ label: "", field_type: "text", options: "", required: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = () => api.get(`/api/groups/${groupId}/project-fields`).then(setFields)
    .catch(e => setError(e.message));
  // `load` is redefined every render; listing it would refetch on
  // every keystroke elsewhere in the component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFields(null); load(); }, [groupId]);

  const add = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/groups/${groupId}/project-fields`, {
        label: draft.label,
        field_type: draft.field_type,
        required: draft.required,
        options: draft.field_type === "choice"
          ? draft.options.split(",").map(o => o.trim()).filter(Boolean)
          : null,
      });
      setDraft({ label: "", field_type: "text", options: "", required: false });
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const remove = async field => {
    setBusy(true); setError(null);
    try {
      await api.delete(`/api/groups/${groupId}/project-fields/${field.id}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>Project details</h3>
      <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: 13 }}>
        Extra fields recorded on this group's projects. They appear on the project page and
        nowhere else — another facility's projects are unaffected.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}

      {fields === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
        : fields.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 16px" }}>No extra fields yet.</p>
        : (
        <table className="responsive-table" style={{ borderCollapse: "collapse", width: "100%", fontSize: 13, marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
              <th style={{ padding: "8px 10px" }}>Label</th>
              <th style={{ padding: "8px 10px" }}>Type</th>
              <th style={{ padding: "8px 10px" }}>Required</th>
              <th style={{ padding: "8px 10px" }}>Key</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fields.map(f => (
              <tr key={f.id} style={{ borderBottom: "1px solid #f3f4f6",
                                      opacity: f.is_active ? 1 : 0.55 }}>
                <td data-label="Label" style={{ padding: "8px 10px" }}>
                  {f.label}
                  {!f.is_active && <span style={{ color: "#6b7280", fontSize: 12 }}> · retired</span>}
                </td>
                <td data-label="Type" style={{ padding: "8px 10px", color: "#6b7280" }}>
                  {(TYPES.find(t => t.value === f.field_type) || {}).label || f.field_type}
                  {f.options?.length ? ` (${f.options.join(", ")})` : ""}
                </td>
                <td data-label="Required" style={{ padding: "8px 10px", color: "#6b7280" }}>{f.required ? "Yes" : "—"}</td>
                <td data-label="Key" style={{ padding: "8px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#6b7280" }}>
                  {f.key}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>
                  {canEdit && f.is_active && (
                    <Btn variant="secondary" onClick={() => remove(f)} disabled={busy}
                         style={{ padding: "4px 10px", fontSize: 12 }}>Remove</Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canEdit && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, alignItems: "end" }}>
            <Field label="New field">
              <Input value={draft.label} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
                     placeholder="Organism, Sample count…" />
            </Field>
            <Field label="Type">
              <Select options={TYPES} value={draft.field_type}
                      onChange={e => setDraft(d => ({ ...d, field_type: e.target.value }))} />
            </Field>
            {draft.field_type === "choice" && (
              <Field label="Options" hint="Comma separated">
                <Input value={draft.options}
                       onChange={e => setDraft(d => ({ ...d, options: e.target.value }))}
                       placeholder="Illumina, Nanopore" />
              </Field>
            )}
            <Field label="Required">
              <Toggle checked={draft.required}
                      onChange={v => setDraft(d => ({ ...d, required: v }))} />
            </Field>
          </div>
          <div style={{ marginTop: 12 }}>
            <Btn onClick={add} disabled={busy || !draft.label.trim()}>
              {busy ? <><Spinner /> Working…</> : "Add field"}
            </Btn>
          </div>
          <p style={{ margin: "10px 0 0", color: "#6b7280", fontSize: 12 }}>
            A field that projects have already answered is retired rather than deleted — what
            people typed is not thrown away. The key is fixed when the field is created, so
            renaming the label later keeps every answer attached.
          </p>
        </>
      )}
    </Card>
  );
}
